import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Mail, Check, X } from "lucide-react";

/**
 * Banner shown on Dashboard when the current user has an addressed pending
 * invitation. Disappears once accepted, dismissed, or the inviter cancels.
 *
 * Polls /couple/incoming on mount + every 60s while visible (light footprint).
 */
export default function IncomingInvitationBanner() {
  const { user, refresh } = useAuth();
  const [invs, setInvs] = useState([]);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    // Skip entirely once the user joins a household
    if (!user || user.household_id) {
      setInvs([]);
      return;
    }
    try {
      const { data } = await api.get("/couple/incoming");
      setInvs(data || []);
    } catch (_) {
      setInvs([]);
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const accept = async (inv) => {
    setRunning(true);
    try {
      await api.post("/couple/accept", { code: inv.code });
      toast.success(`Te uniste al espacio compartido con ${inv.from_user_name || inv.from_user_email}`);
      await refresh?.();
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo aceptar la invitación");
    } finally {
      setRunning(false);
    }
  };

  if (!invs.length) return null;
  const inv = invs[0]; // Show only the most recent one to avoid clutter

  return (
    <section
      className="px-6 mt-4 animate-fade-up"
      data-testid="incoming-invitation-banner"
    >
      <div className="bg-sage-50 border border-sage-200 rounded-2xl px-4 py-3.5">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-white text-sage-700 flex items-center justify-center shrink-0">
            <Mail size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-sage-700">
              Invitación pendiente
            </p>
            <p className="text-sm font-bold text-ink mt-0.5 truncate">
              {inv.from_user_name || inv.from_user_email}
            </p>
            <p className="text-[11px] text-soft leading-snug mt-1">
              Te invitó a compartir un espacio de dinero común en FiPer.
            </p>
            <div className="flex gap-2 mt-2.5">
              <button
                type="button"
                onClick={() => accept(inv)}
                disabled={running}
                data-testid="incoming-invitation-accept"
                className="inline-flex items-center gap-1.5 bg-sage-900 text-bone text-xs font-bold rounded-full px-3.5 h-8 hover:bg-sage-900/90 disabled:opacity-60"
              >
                <Check size={12} strokeWidth={2.8} />
                {running ? "Uniendo…" : "Aceptar"}
              </button>
              <button
                type="button"
                onClick={() => setInvs([])}
                data-testid="incoming-invitation-dismiss"
                className="inline-flex items-center gap-1.5 bg-white border border-line text-soft text-xs font-bold rounded-full px-3.5 h-8 hover:text-ink"
              >
                <X size={12} /> Ahora no
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
